import React, { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';

import { useMessage } from '../../shared/hooks/hooks';

import { formatMilesInThousands } from '../../shared/utils/format/format';

import { TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG } from '../../shared/constants/messages';

import './tread-life-estimate.scss';

interface TreadLifeEstimateProps {
  className?: string;
  medianMileage: number | null | undefined;
  minMileage: number | null | undefined;
}

const MIN_WIDTH = 235;

function TreadLifeEstimate(props: TreadLifeEstimateProps) {
  const { className, minMileage, medianMileage } = props;
  const fallbackMessage =
    'Tread life is an estimate based on actual mileage for this tire on your vehicle model, in your area, with proper maintenance.';
  const message = useMessage([TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG]);
  const tooltipMessage =
    message?.[TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG];
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldAbbreviate, setShouldAbbreviate] = useState(false);

  const checkShouldAbbreviate = useCallback(() => {
    if (containerRef.current) {
      const { width: containerRefWidth } =
        containerRef.current.getBoundingClientRect();

      setShouldAbbreviate(containerRefWidth < MIN_WIDTH);
    }
  }, []);

  useEffect(() => {
    checkShouldAbbreviate();
    window.addEventListener('resize', checkShouldAbbreviate);

    return () => window.removeEventListener('resize', checkShouldAbbreviate);
  }, [checkShouldAbbreviate]);
  if (!medianMileage || medianMileage === 0) {
    return null;
  }

  return (
    <div ref={containerRef} styleName="wrapper">
      <Typography
        className={className}
        size="small"
        styleName="estimate"
        tag="div"
      >
        {`${formatMilesInThousands(minMileage)}-${formatMilesInThousands(
          medianMileage
        )}
        tread life ${shouldAbbreviate ? 'est.' : 'estimate'}`}

        <Tooltip
          content={tooltipMessage || fallbackMessage}
          styleName="tooltip"
        />
      </Typography>
    </div>
  );
}

export default TreadLifeEstimate;
